import React, { useState, useContext, useMemo } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Select from 'react-select'
import { toast } from 'react-toastify'
import UserContext from 'context/user-context'
import { useAdminMunicipalities } from 'api/hooks/use-admin-municipalities'
import { useSwitchMunicipality } from 'api/hooks/use-admin-municipality-switch'

const MunicipalitySwitcher = ({ show, onHide }) => {
  const { municipality_id, municipality_name, is_admin, switchable_municipalities } =
    useContext(UserContext)
  const [selectedId, setSelectedId] = useState('')

  const { data: municipalitiesResponse, isLoading } = useAdminMunicipalities({
    params: { perPage: 1000 },
    options: { enabled: show && Boolean(is_admin) },
  })
  const municipalities = is_admin
    ? municipalitiesResponse?.data || []
    : switchable_municipalities || []

  const options = useMemo(
    () =>
      municipalities.map((m) => ({
        value: m.id,
        label: m.id === municipality_id ? `${m.name} (current)` : m.name,
        isDisabled: m.id === municipality_id,
      })),
    [municipalities, municipality_id]
  )

  const selectedOption =
    options.find((o) => String(o.value) === String(selectedId)) || null

  const { mutate: switchMunicipality, isLoading: isSwitching } =
    useSwitchMunicipality({
      isAdmin: Boolean(is_admin),
      options: {
        onError: () => {
          toast.error('Failed to switch municipality')
        },
      },
    })

  const handleConfirm = () => {
    if (selectedId) {
      switchMunicipality(selectedId)
    }
  }

  const handleExited = () => {
    setSelectedId('')
  }

  return (
    <Modal show={show} onHide={onHide} onExited={handleExited}>
      <Modal.Header closeButton>
        <Modal.Title>Change Municipality</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-2">
          Current municipality: <strong>{municipality_name}</strong>
        </p>
        <p className="text-muted small mb-3">
          Switching will scope all data (dashboards, reports, exports, etc.) to
          the selected municipality.
        </p>

        <Form.Group className="mb-3">
          <Form.Label>Select Municipality</Form.Label>
          {is_admin && isLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Select
              inputId="municipality-switcher-select"
              options={options}
              value={selectedOption}
              onChange={(opt) => setSelectedId(opt ? opt.value : '')}
              isOptionDisabled={(opt) => opt.isDisabled}
              isClearable
              placeholder="Search municipalities..."
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : null
              }
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onHide} disabled={isSwitching}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!selectedId || isSwitching}
        >
          {isSwitching ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Switching...
            </>
          ) : (
            'Confirm Switch'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MunicipalitySwitcher
